// src/store/slices/paymentSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  getAllPaymentsThunk,
  getPendingPaymentsThunk,
  createPaymentThunk,
  getPaymentByIdThunk,
  getPlanPaymentsThunk,
  getCustomerPaymentsThunk,
  verifyPaymentThunk,
  rejectPaymentThunk,
  clearPaymentError,
} from "../../thunks/jewelleryPlan/paymentThunks";

const initialState = {
  payments: [],
  pendingPayments: [],
  selectedPayment: null,
  planPayments: [],
  customerPayments: [],
  uploadProgress: 0,
  filters: {
    status: null,
    branchId: null,
    dateFrom: null,
    dateTo: null,
  },
  pagination: {
    total: 0,
    page: 1,
    pages: 0,
    pendingCount: 0,
  },
  pendingSummary: {
    total: 0,
    totalAmount: 0,
    averageAge: 0,
    aging: {},
  },
  loading: false,
  error: null,
  success: null,
};

const paymentSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    setPaymentFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    clearSelectedPayment: (state) => {
      state.selectedPayment = null;
    },
    setSelectedPayment: (state, action) => {
      state.selectedPayment = action.payload;
    },
    clearPaymentMessages: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Payments
      .addCase(getAllPaymentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPaymentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload.data || [];
        state.pagination = action.payload.pagination || {
          total: action.payload.data?.length || 0,
          page: 1,
          pages: 1,
        };
        // Count pending payments
        const pendingCount = (action.payload.data || []).filter(
          (p) => p.status === "PENDING_REVIEW"
        ).length;
        state.pagination.pendingCount = pendingCount;
      })
      .addCase(getAllPaymentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch payments";
      })

      // Get Pending Payments - FIXED
      .addCase(getPendingPaymentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPendingPaymentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        // action.payload is already the data object from response.data
        state.pendingPayments = action.payload.payments || [];
        state.pendingSummary = action.payload.summary || {
          total: 0,
          totalAmount: 0,
          averageAge: 0,
          aging: {},
        };
      })
      .addCase(getPendingPaymentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch pending payments";
        // Keep state initialized even on error
        state.pendingPayments = [];
        state.pendingSummary = {
          total: 0,
          totalAmount: 0,
          averageAge: 0,
          aging: {},
        };
      })

      // Create Payment
      .addCase(createPaymentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = "Payment recorded successfully";
        state.payments.unshift(action.payload.data);
        state.pagination.total += 1;
        if (action.payload.data.status === "PENDING_REVIEW") {
          state.pagination.pendingCount += 1;
          state.pendingPayments.unshift(action.payload.data);
        }
      })
      .addCase(createPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create payment";
      })

      // Get Payment By ID
      .addCase(getPaymentByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPaymentByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPayment = action.payload.data;
      })
      .addCase(getPaymentByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch payment";
      })

      // Get Plan Payments
      .addCase(getPlanPaymentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlanPaymentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.planPayments = action.payload.data || [];
      })
      .addCase(getPlanPaymentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch plan payments";
      })

      // Get Customer Payments
      .addCase(getCustomerPaymentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerPaymentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.customerPayments = action.payload.data || [];
      })
      .addCase(getCustomerPaymentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch customer payments";
      })

      // Verify Payment
      .addCase(verifyPaymentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(verifyPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = "Payment verified successfully";

        // Remove from pending payments
        state.pendingPayments = state.pendingPayments.filter(
          (payment) => payment._id !== action.payload.data._id
        );

        // Update in payments list
        const index = state.payments.findIndex(
          (payment) => payment._id === action.payload.data._id
        );
        if (index !== -1) {
          state.payments[index] = action.payload.data;
        }

        // Update counters
        if (state.pendingSummary.total > 0) {
          state.pendingSummary.total -= 1;
        }
        if (state.pagination.pendingCount > 0) {
          state.pagination.pendingCount -= 1;
        }
      })
      .addCase(verifyPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to verify payment";
      })

      // Reject Payment
      .addCase(rejectPaymentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(rejectPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = "Payment rejected";

        // Remove from pending payments
        state.pendingPayments = state.pendingPayments.filter(
          (payment) => payment._id !== action.payload.data._id
        );

        // Update in payments list
        const index = state.payments.findIndex(
          (payment) => payment._id !== action.payload.data._id
        );
        if (index !== -1) {
          state.payments[index] = action.payload.data;
        }

        // Update counters
        if (state.pendingSummary.total > 0) {
          state.pendingSummary.total -= 1;
        }
        if (state.pagination.pendingCount > 0) {
          state.pagination.pendingCount -= 1;
        }
      })
      .addCase(rejectPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reject payment";
      })

      // Clear Error
      .addCase(clearPaymentError, (state) => {
        state.error = null;
      });
  },
});

export const {
  setPaymentFilters,
  setUploadProgress,
  clearSelectedPayment,
  setSelectedPayment,
  clearPaymentMessages,
} = paymentSlice.actions;

export default paymentSlice.reducer;
